/**
 * Settings > Project > Integrations — the project's outbound connectors.
 *
 * Slack lives under Settings → Organization → Slack (one workspace for every
 * project in the org). This list is Linear, GitHub, Cursor, and Claude Code.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { X as XIcon } from 'lucide-react';

import axiosInstance from 'shared/services/api/axiosInstance';
import { useBreadcrumbActions } from 'shared/context/BreadcrumbContext';
import { useTeam } from 'shared/context/teamContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import { settingsSolidButtonClass, settingsTableShellClass } from 'features/settings/components/settingsTheme';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';
import CursorIntegrationPage from 'features/integrations/CursorIntegrationPage';
import ClaudeCodeIntegrationPage from 'features/integrations/ClaudeCodeIntegrationPage';
import GithubIntegrationPage from 'features/integrations/GithubIntegrationPage';

const publicAsset = (p) => `${(process.env.PUBLIC_URL || '').replace(/\/?$/, '')}${p}`;

/**
 * `kind` drives the connect affordance:
 *   oauth  — POST <start> then hand the browser to the provider
 *   modal  — open `Wizard` in a dialog
 *   page   — push an inner view (GitHub only)
 */
const CONNECTORS = [
  {
    id: 'linear',
    label: 'Linear',
    logo: '/icons/linear.png',
    kind: 'oauth',
    status: '/integrations/linear',
    start: '/integrations/linear/start',
    param: 'team_id',
    callbackParam: 'linear',
    describe: (c) => c.linear_organization_name || c.linear_organization_id,
  },
  {
    id: 'github',
    label: 'GitHub',
    logo: '/icons/github.png',
    // Solid near-black mark — it disappears against a dark surface. The old
    // catalog flagged this too, but its `darkInvert` applied a class
    // (icon-dark-adaptive) that was never defined in any stylesheet, so the
    // logo had been unreadable in dark mode the whole time.
    darkInvert: true,
    kind: 'page',
    status: '/integrations/github',
    param: 'team_id',
    // getMine returns { integration } rather than { connected }.
    isConnected: (d) => !!d?.integration,
    describe: (c) => c.integration?.account_login,
    Page: GithubIntegrationPage,
  },
  {
    id: 'cursor',
    label: 'Cursor',
    logo: '/icons/cursor.svg',
    kind: 'modal',
    status: '/integrations/cursor',
    param: 'teamId',
    Wizard: CursorIntegrationPage,
  },
  {
    id: 'claudecode',
    label: 'Claude Code',
    logo: '/icons/claude.png',
    kind: 'modal',
    status: '/integrations/claudecode',
    param: 'teamId',
    Wizard: ClaudeCodeIntegrationPage,
  },
];

function humanError(e) {
  return e?.response?.data?.error || e?.message || 'Request failed';
}

// ---------------------------------------------------------------------------
// Wizard modal — wraps the existing Cursor / Claude Code pages
// ---------------------------------------------------------------------------

function WizardModal({ connector, teamId, onClose }) {
  const Wizard = connector.Wizard;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // The wizard is tall; stop the page behind it scrolling under the scrim.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-6"
      style={{ background: 'var(--surface-overlay)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Connect ${connector.label}`}
        className="my-auto w-full max-w-2xl rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-token-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-4 py-2.5">
          <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">
            {connector.label}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          >
            <XIcon size={14} aria-hidden />
          </button>
        </div>
        {/* The wizard renders its own hero + steps and owns its connection
            state, so it just drops in here unchanged. */}
        <Wizard teamId={teamId} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connector page (GitHub) — hoists its primary action into the top bar
// ---------------------------------------------------------------------------

/**
 * GithubIntegrationPage renders no "Add repo" control of its own: it hands one
 * up through onHeaderChange and expects the host to place it. We put it in the
 * GlobalTopBar's actions slot so it sits inline with the breadcrumb path,
 * rather than stacking a second row of chrome under it.
 */
function ConnectorPage({ connector, teamId }) {
  const Page = connector.Page;
  const [header, setHeader] = useState(null);

  useBreadcrumbActions(
    header?.onAddRepo ? (
      <button
        type="button"
        onClick={header.onAddRepo}
        disabled={header.busy}
        className={settingsSolidButtonClass}
      >
        Add repo
      </button>
    ) : null,
  );

  return <Page teamId={teamId} onHeaderChange={setHeader} />;
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function IntegrationsPanel({ connector }) {
  const { selectedTeam } = useTeam();
  const location = useLocation();
  const navigate = useNavigate();
  const { team } = useParams();
  const dialog = useAppDialog();
  const { can } = usePermissions();

  const basePath = `/${encodeURIComponent(team || selectedTeam?.name || '')}/settings/integrations`;
  const teamId = selectedTeam?.id || selectedTeam?._id || null;
  const canManage = can(PERMISSIONS.INTEGRATIONS_DEVELOPER_MANAGE);

  const [state, setState] = useState({});   // id -> { connected, detail }
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [wizard, setWizard] = useState(null); // connector opened in a modal

  const refresh = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    // allSettled, not all: one connector erroring must not blank the whole
    // list — that row just reads as not connected.
    const results = await Promise.allSettled(
      CONNECTORS.map((c) =>
        axiosInstance.get(c.status, { params: { [c.param]: teamId } }).then((r) => r.data),
      ),
    );
    const next = {};
    CONNECTORS.forEach((c, i) => {
      const r = results[i];
      const data = r.status === 'fulfilled' ? r.value : null;
      const connected = data ? (c.isConnected ? c.isConnected(data) : !!data.connected) : false;
      next[c.id] = {
        connected,
        detail: connected && c.describe ? c.describe(data) : null,
        needs_reauthorize: !!(connected && data?.needs_reauthorize),
        // A stored install whose token no longer works.
        needs_reconnect: !!(connected && data?.needs_reconnect),
      };
    });
    setState(next);
    setLoading(false);
  }, [teamId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Slack moved to Settings → Organization → Slack. Old Project Integrations
  // deep links and OAuth returns (?integration=slack, ?slack=connected) land here.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('integration') === 'slack' || params.has('slack') || params.has('org_slack')) {
      const next = new URLSearchParams(location.search);
      next.delete('integration');
      const qs = next.toString();
      navigate(
        `/${encodeURIComponent(team || selectedTeam?.name || '')}/settings/org-slack${qs ? `?${qs}` : ''}`,
        { replace: true },
      );
    }
  }, [location.search, navigate, team, selectedTeam?.name]);

  // Slack/Linear bounce back through the OAuth callback with ?slack=connected
  // (or ?linear=error&reason=…). Surface it, then re-read the real state.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    for (const c of CONNECTORS) {
      if (!c.callbackParam) continue;
      const status = params.get(c.callbackParam);
      if (status === 'connected') {
        setBanner({ kind: 'ok', text: `${c.label} connected.` });
        refresh();
      } else if (status === 'error') {
        // A workspace already linked to another project is the one refusal a
        // person can act on, so it says what to do instead of showing a code.
        const reason = params.get('reason') || 'unknown';
        if (reason === 'workspace_linked_elsewhere') {
          const holder = params.get('linked_team');
          setBanner({
            kind: 'err',
            text: holder
              ? `That Slack workspace is already connected to ${holder}. Disconnect it there first: a workspace can belong to one project at a time.`
              : 'That Slack workspace is already connected to another project. Disconnect it there first: a workspace can belong to one project at a time.',
          });
        } else {
          setBanner({ kind: 'err', text: `${c.label} connection failed: ${reason}` });
        }
      }
    }
  }, [location.search, refresh]);

  // Deep link: ?integration=<id> opens that connector directly. Heal's "Connect
  // Cursor" CTA relies on this — the old Integration Manager honoured the same
  // param, and without it those buttons would just dump you on the list.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('integration');
    if (!id) return;
    const c = CONNECTORS.find((x) => x.id === id);
    if (!c) return;
    if (c.kind === 'modal') setWizard(c);
    else if (c.kind === 'page') navigate(`${basePath}/${c.id}`, { replace: true });
  }, [location.search, navigate, basePath]);

  async function connect(c) {
    if (c.kind === 'modal') { setWizard(c); return; }
    if (c.kind === 'page')  { navigate(`${basePath}/${c.id}`); return; }

    setBusyId(c.id);
    setError(null);
    try {
      // Send the current in-app path so the callback returns here rather than
      // to FRONTEND_URL, which has no /:team prefix.
      const return_to = `${window.location.pathname}${window.location.search || ''}`;
      const { data } = await axiosInstance.post(
        c.start,
        { return_to },
        { params: { [c.param]: teamId } },
      );
      if (data?.url) { window.location.href = data.url; return; }
      setError(`Could not start ${c.label} authorization.`);
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function disconnect(c) {
    const ok = await dialog.confirm(
      `Any agent or workflow step using ${c.label} will fail until you reconnect it.`,
      { title: `Disconnect ${c.label}?`, confirmLabel: 'Disconnect', cancelLabel: 'Cancel', danger: true },
    );
    if (!ok) return;
    setBusyId(c.id);
    setError(null);
    try {
      await axiosInstance.delete(c.status, { params: { [c.param]: teamId } });
      await refresh();
      setBanner({ kind: 'ok', text: `${c.label} disconnected.` });
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusyId(null);
    }
  }

  const rows = useMemo(() => CONNECTORS, []);

  // GitHub (and only GitHub) owns a page. It's a real route
  // (/settings/integrations/github), so the breadcrumb is the way back —
  // no in-panel back button, and Settings renders the path for us.
  if (connector) {
    const c = CONNECTORS.find((x) => x.id === connector && x.kind === 'page');
    if (c) return <ConnectorPage connector={c} teamId={teamId} />;
  }

  return (
    <div className="space-y-3">
      {!canManage && (
        <p className="text-[12px] text-[color:var(--text-muted)]">Only owners, admins and developers can change these.</p>
      )}

      {banner && (
        <p
          role="status"
          className="text-[12px]"
          style={{ color: banner.kind === 'ok' ? 'var(--feedback-success)' : 'var(--feedback-error)' }}
        >
          {banner.text}
        </p>
      )}

      {error && <p role="alert" className="text-[12px] text-[color:var(--feedback-error)]">{error}</p>}

      {loading && Object.keys(state).length === 0 ? (
        <BrandLoaderInline label="Loading integrations" />
      ) : (
      <ul className={settingsTableShellClass}>
        {rows.map((c) => {
          const s = state[c.id] || {};
          const busy = busyId === c.id;
          const warn = s.connected && (s.needs_reconnect || s.needs_reauthorize);
          const status = !s.connected
            ? 'Not connected'
            : s.needs_reconnect
              ? 'Connection expired'
              : s.needs_reauthorize
                ? 'Reconnect to grant mention permission'
                : s.detail ? `Connected · ${s.detail}` : 'Connected';
          return (
            <li
              key={c.id}
              className="flex h-12 items-center gap-3 border-b border-[color:var(--border-subtle)] px-3 last:border-b-0"
            >
              <img
                src={publicAsset(c.logo)}
                alt=""
                aria-hidden
                className={`h-5 w-5 shrink-0 object-contain${c.darkInvert ? ' dark:invert' : ''}`}
                loading="lazy"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-[color:var(--text-primary)]">{c.label}</p>
                <p
                  className="truncate text-[11px] leading-snug"
                  style={{ color: warn ? 'var(--feedback-warning)' : 'var(--text-muted)' }}
                >
                  {status}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {c.kind === 'page' ? (
                  <button
                    type="button"
                    onClick={() => navigate(`${basePath}/${c.id}`)}
                    className={activityNeutralButtonClass}
                  >
                    {s.connected ? 'Manage' : 'Connect'}
                  </button>
                ) : s.connected ? (
                  <>
                    {s.needs_reauthorize && canManage && (
                      <button type="button" onClick={() => connect(c)} disabled={busy} className={activityNeutralButtonClass}>
                        Reconnect
                      </button>
                    )}
                    {canManage && (
                      <button type="button" onClick={() => disconnect(c)} disabled={busy} aria-label={`Disconnect ${c.label}`} className={activityNeutralButtonClass}>
                        {busy ? 'Working…' : 'Disconnect'}
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => connect(c)}
                    disabled={busy || !canManage}
                    className={settingsSolidButtonClass}
                  >
                    {busy ? 'Working…' : 'Connect'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      )}

      {wizard && (
        <WizardModal
          connector={wizard}
          teamId={teamId}
          onClose={() => { setWizard(null); refresh(); }}
        />
      )}
    </div>
  );
}
